import styled, { css } from 'styled-components';

const ALIGN_MAP: Record<string, string> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
  stretch: 'stretch',
};

const JUSTIFY_MAP: Record<string, string> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  'space-around': 'space-around',
  'space-between': 'space-between',
  'space-evenly': 'space-evenly',
};

const BREAKPOINT_MIN: Record<string, number> = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

function buildResponsiveAlign(align: string | Record<string, string>): string {
  if (typeof align === 'string') {
    return ALIGN_MAP[align] ? `align-items: ${ALIGN_MAP[align]};` : '';
  }
  let result = '';
  for (const [bp, value] of Object.entries(align)) {
    const mapped = ALIGN_MAP[value];
    if (!mapped) continue;
    if (bp === 'xs') {
      result += `align-items: ${mapped};`;
    } else if (BREAKPOINT_MIN[bp]) {
      result += `@media (min-width: ${BREAKPOINT_MIN[bp]}px) { align-items: ${mapped}; }`;
    }
  }
  return result;
}

function buildResponsiveJustify(justify: string | Record<string, string>): string {
  if (typeof justify === 'string') {
    return JUSTIFY_MAP[justify] ? `justify-content: ${JUSTIFY_MAP[justify]};` : '';
  }
  let result = '';
  for (const [bp, value] of Object.entries(justify)) {
    const mapped = JUSTIFY_MAP[value];
    if (!mapped) continue;
    if (bp === 'xs') {
      result += `justify-content: ${mapped};`;
    } else if (BREAKPOINT_MIN[bp]) {
      result += `@media (min-width: ${BREAKPOINT_MIN[bp]}px) { justify-content: ${mapped}; }`;
    }
  }
  return result;
}

interface StyledRowProps {
  $hGutter: number;
  $vGutter: number;
  $wrap: boolean;
  $align?: string | Record<string, string>;
  $justify?: string | Record<string, string>;
}

export const StyledRow = styled.div<StyledRowProps>`
  display: flex;
  flex-direction: row;
  min-width: 0;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  ${({ $hGutter }) =>
    $hGutter
      ? css`
          margin-left: ${-$hGutter / 2}px;
          margin-right: ${-$hGutter / 2}px;
        `
      : ''}
  ${({ $vGutter }) =>
    $vGutter
      ? css`
          row-gap: ${$vGutter}px;
        `
      : ''}
  ${({ $align }) => ($align ? buildResponsiveAlign($align) : '')}
  ${({ $justify }) => ($justify ? buildResponsiveJustify($justify) : '')}
`;

interface StyledColProps {
  $hGutter: number;
  $colCss: string;
}

export const StyledCol = styled.div<StyledColProps>`
  position: relative;
  max-width: 100%;
  min-height: 1px;
  box-sizing: border-box;
  ${({ $hGutter }) =>
    $hGutter
      ? css`
          padding-left: ${$hGutter / 2}px;
          padding-right: ${$hGutter / 2}px;
        `
      : ''}
  ${({ $colCss }) => $colCss}
`;
